import { useEffect, useMemo, useState } from "react";
import { batchStatusMeta, feeStatusMeta, fmtMoney, Pill } from "./Master";
import { SweetAlertShow } from "../../../master/Master";
import { formatToDate } from "../../../master/FormatToDateTime";
import { studentBatchNFeeUpdateDiscount } from "../../../../api/StudentDetails";
import useAppConfig from "../../../config/useAppConfig";
import usePrivateLink from "../../../config/usePrivateLink";

const NON_DISCOUNTABLE_FEE_TYPES = [
  "Registration Fee",
  "Previous Session Fees",
];

export default function FeeDetailModal({ batch, onClose, onUpdated }) {
  const [mode, setMode] = useState("view");
  const [saving, setSaving] = useState(false);
  const config = useAppConfig("RTE", "rte_workflow");
  const [feeItems, setFeeItems] = useState([]);
  const [rteStatus, setRteStatus] = useState("NON-RTE");
  const hasAccess = usePrivateLink();

  const [discountData, setDiscountData] = useState({
    type: "fixed",
    value: "",
  });
  const [discountRemark, setDiscountRemark] = useState(
    batch?.discount_remark || "",
  );

  useEffect(() => {
    if (mode === "discount" && batch?.student_fee_details) {
      setFeeItems(
        batch.student_fee_details.map((f) => ({
          id: f.id,
          fee_type: f.fee_type,
          actual_amount: Number(f.actual_amount || 0),
          final_amount: Number(f.final_amount || 0),
          paid_amount: Number(f.paid_amount || 0),
        })),
      );
      setRteStatus(batch?.rte_status);
      setDiscountData({ type: "fixed", value: "" });
      setDiscountRemark(batch?.discount_remark || "");
    }
  }, [mode, batch]);

  const totalActual = useMemo(
    () => feeItems.reduce((sum, f) => sum + Number(f.actual_amount || 0), 0),
    [feeItems],
  );

  const totalFinal = useMemo(
    () => feeItems.reduce((sum, f) => sum + Number(f.final_amount || 0), 0),
    [feeItems],
  );

  const totalPaid = useMemo(
    () => feeItems.reduce((sum, f) => sum + Number(f.paid_amount || 0), 0),
    [feeItems],
  );

  if (!batch) return null;

  const feeDetails = batch?.student_fee_details || [];
  const prevSessionFees = feeDetails.filter(
    (f) => f.fee_type === "Previous Session Fees",
  );
  const statusMeta = batchStatusMeta(batch?.status);

  const totalDiscount = Math.max(totalActual - totalFinal, 0);

  const calculateDiscount = (type, value) => {
    const totalFee = totalActual;

    let discountValue = Number(value || 0);

    if (type === "fixed") {
      discountValue = Math.min(discountValue, totalFee);
    } else {
      discountValue = Math.min(discountValue, 100);
    }

    let expectedFinal =
      type === "fixed"
        ? totalFee - discountValue
        : totalFee - (totalFee * discountValue) / 100;

    expectedFinal = Math.max(0, Math.round(expectedFinal));

    let updatedFees = feeItems.map((fee) => {
      const amount = Number(fee.actual_amount);

      if (NON_DISCOUNTABLE_FEE_TYPES.includes(fee.fee_type)) {
        return { ...fee, final_amount: amount };
      }

      let finalAmount = amount;

      if (type === "fixed") {
        const ratio = totalFee > 0 ? amount / totalFee : 0;
        finalAmount = amount - discountValue * ratio;
      } else {
        finalAmount = amount - (amount * discountValue) / 100;
      }

      finalAmount = Math.round(finalAmount);
      finalAmount = Math.max(fee.paid_amount, finalAmount);

      return { ...fee, final_amount: finalAmount };
    });

    let currentTotal = updatedFees.reduce(
      (sum, fee) => sum + Number(fee.final_amount || 0),
      0,
    );

    let diff = expectedFinal - currentTotal;

    if (diff !== 0) {
      for (let i = updatedFees.length - 1; i >= 0; i--) {
        if (!NON_DISCOUNTABLE_FEE_TYPES.includes(updatedFees[i].fee_type)) {
          const adjusted = Number(updatedFees[i].final_amount) + diff;
          updatedFees[i].final_amount = Math.max(
            updatedFees[i].paid_amount,
            adjusted,
          );
          break;
        }
      }
    }

    setFeeItems(updatedFees);
  };

  const handleDiscountTypeChange = (type) => {
    setDiscountData((prev) => ({ ...prev, type }));
    calculateDiscount(type, discountData.value);
  };

  const handleRteStatus = (status) => {
    setRteStatus(status);

    let rteConfig = [];

    try {
      rteConfig = JSON.parse(config?.value || "[]");
    } catch (e) {
      rteConfig = [];
    }

    const updatedFees = feeItems.map((fee) => {
      const key = fee.fee_type
        ?.toLowerCase()
        ?.trim()
        ?.replace(/[^a-z0-9]+/g, "_")
        ?.replace(/^_|_$/g, "");

      const isFree = rteConfig?.find(
        (x) => x.key === key && Number(x.value) === 1,
      );

      if (status === "RTE") {
        return {
          ...fee,
          final_amount: isFree
            ? Math.max(Number(fee.paid_amount || 0), 0)
            : Number(fee.actual_amount || 0),
        };
      }

      return {
        ...fee,
        final_amount: Number(fee.actual_amount || 0),
      };
    });

    setFeeItems(updatedFees);

    if (discountData.value) {
      setTimeout(() => {
        calculateDiscount(discountData.type, discountData.value);
      }, 0);
    }
  };

  const handleDiscountValueChange = (value) => {
    const numValue = Number(value || 0);
    setDiscountData((prev) => ({ ...prev, value: numValue }));
    calculateDiscount(discountData.type, numValue);
  };

  const handleUpdateDiscount = async () => {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("student_batch_n_fee_id", batch.id);
      formData.append("discount_remark", discountRemark);
      formData.append("rte_status", rteStatus);

      feeItems.forEach((fee, index) => {
        formData.append(`fee_type[${index}]`, fee.fee_type);
        formData.append(`total[${index}]`, fee.actual_amount);
        formData.append(`final[${index}]`, fee.final_amount);
      });

      const response = await studentBatchNFeeUpdateDiscount(formData);
      if (response.data.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
        setMode("view");
        onUpdated?.();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal d-block"
      style={{ background: "rgba(0,0,0,0.5)" }}
      tabIndex={-1}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        onClick={(e) => e?.stopPropagation()}
      >
        <div className="modal-content rounded-4 border-0 overflow-hidden">
          {/* Header */}
          <div className="modal-header border-0 pb-0">
            <div>
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <i className="ri-wallet-3-line text-primary"></i>
                Fee Details
              </h5>
              <div className="d-flex flex-wrap gap-2 mt-2">
                <Pill
                  icon="ri-book-open-line"
                  color="primary"
                  label="Course"
                  value={batch?.course?.title || "-"}
                />
                <Pill
                  icon="ri-team-line"
                  color="success"
                  label="Batch"
                  value={batch?.batch?.title || "-"}
                />
                <span
                  className={`badge rounded-pill bg-${statusMeta.color} d-inline-flex align-items-center gap-1 px-3 py-2`}
                >
                  <i className={statusMeta.icon}></i>
                  {statusMeta.label}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div className="modal-body">
            {mode === "view" && (
              <>
                {/* Summary grid */}
                <div className="row g-3 mb-3">
                  <div className="col-6 col-md-3">
                    <div
                      className="p-2 rounded-3 text-center"
                      style={{ background: "#f8f9fb" }}
                    >
                      <small className="text-secondary d-block">
                        Total Fee
                      </small>
                      <span className="fw-semibold">
                        {fmtMoney(batch?.total_fee)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-2 rounded-3 text-center bg-warning bg-opacity-10">
                      <small className="text-warning d-block">Discount</small>
                      <span className="fw-semibold text-warning">
                        {fmtMoney(batch?.total_discount)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div
                      className="p-2 rounded-3 text-center"
                      style={{ background: "#f8f9fb" }}
                    >
                      <small className="text-secondary d-block">
                        Final Fee
                      </small>
                      <span className="fw-semibold">
                        {fmtMoney(batch?.final_fee)}
                      </span>
                    </div>
                  </div>
                  <div className="col-6 col-md-3">
                    <div className="p-2 rounded-3 text-center bg-success bg-opacity-10">
                      <small className="text-success d-block">Paid</small>
                      <span className="fw-semibold text-success">
                        {fmtMoney(batch?.total_paid)}
                      </span>
                    </div>
                  </div>
                </div>

                {batch?.discount_remark && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <i className="ri-message-2-line text-secondary"></i>
                    <small className="text-secondary">
                      Discount Remark:{" "}
                      <span className="text-dark">{batch.discount_remark}</span>
                    </small>
                  </div>
                )}

                {/* Fee structure table */}
                {feeDetails.length > 0 && (
                  <>
                    <small className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2">
                      <i className="ri-file-list-3-line"></i> FEE STRUCTURE
                    </small>
                    <div className="table-responsive mb-3">
                      <table className="table table-sm table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Fee Type</th>
                            <th>Total</th>
                            <th>Discount</th>
                            <th>Final</th>
                            <th>Paid</th>
                            <th>Due</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feeDetails.map((f) => {
                            const meta = feeStatusMeta(
                              f.paid_amount,
                              f.final_amount,
                            );
                            return (
                              <tr key={f.id}>
                                <td>
                                  <span className="d-flex align-items-center gap-1">
                                    <i className="ri-price-tag-3-line text-secondary"></i>
                                    {f.fee_type}
                                  </span>
                                </td>
                                <td>{fmtMoney(f.actual_amount)}</td>
                                <td className="text-warning">
                                  {fmtMoney(f.discount)}
                                </td>
                                <td>{fmtMoney(f.final_amount)}</td>
                                <td className="text-success">
                                  {fmtMoney(f.paid_amount)}
                                </td>
                                <td className="text-danger">
                                  {fmtMoney(f.due_amount)}
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${meta.color} bg-opacity-10 text-${meta.color} d-inline-flex align-items-center gap-1`}
                                  >
                                    <i className={meta.icon}></i>
                                    {meta.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {prevSessionFees.length > 0 && (
                  <div
                    className="p-3 rounded-3 mb-3"
                    style={{ background: "#f8f9fb" }}
                  >
                    <small className="text-secondary fw-semibold d-flex align-items-center gap-1 mb-2">
                      <i className="ri-history-line"></i> PREVIOUS SESSION FEES
                    </small>
                    <div className="row g-2">
                      {prevSessionFees.map((f) => (
                        <div className="col-md-4" key={f.id}>
                          <small className="text-secondary d-block">
                            Total
                          </small>
                          <span className="fw-semibold">
                            {fmtMoney(f.actual_amount)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div
                  className="d-flex flex-wrap gap-3 pt-2"
                  style={{ borderTop: "1px solid #f0f0f0" }}
                >
                  <small className="text-secondary d-flex align-items-center gap-1">
                    <i className="ri-user-star-line text-primary"></i>
                    Counselor:{" "}
                    <span className="fw-medium text-dark">
                      {batch?.counselor?.name || "-"}
                    </span>
                  </small>
                  <small className="text-secondary d-flex align-items-center gap-1">
                    <i className="ri-calendar-event-line text-primary"></i>
                    Enrolled:{" "}
                    <span className="fw-medium text-dark">
                      {formatToDate(batch?.batch_enroll_date)}
                    </span>
                  </small>
                </div>
              </>
            )}

            {mode === "discount" && (
              <div>
                <div className="row g-3 mb-3">
                  <div className="col-md-4">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1">
                      <i className="ri-price-tag-3-line text-warning"></i>
                      RTE Status
                    </label>
                    <select
                      className="form-select"
                      value={rteStatus}
                      onChange={(e) => handleRteStatus(e.target.value)}
                    >
                      <option value="NON-RTE">NON-RTE</option>
                      <option value="RTE">RTE</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1">
                      <i className="ri-price-tag-3-line text-warning"></i>
                      Discount Type
                    </label>
                    <select
                      className="form-select"
                      value={discountData.type}
                      onChange={(e) => handleDiscountTypeChange(e.target.value)}
                    >
                      <option value="fixed">Fixed ₹</option>
                      <option value="percentage">Percentage %</option>
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label fw-semibold d-flex align-items-center gap-1">
                      <i className="ri-coupon-line text-warning"></i>
                      {discountData.type === "fixed"
                        ? "Discount ₹"
                        : "Discount %"}
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={discountData.value}
                      onChange={(e) =>
                        handleDiscountValueChange(e.target.value)
                      }
                      placeholder="Enter discount value"
                      min="0"
                    />
                  </div>
                </div>

                {feeItems.length > 0 && (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Fee Type</th>
                          <th>Total</th>
                          <th>Discount</th>
                          <th>Final</th>
                          <th>Paid</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeItems.map((f) => {
                          const excluded = NON_DISCOUNTABLE_FEE_TYPES.includes(
                            f.fee_type,
                          );
                          const rowDiscount = Math.max(
                            f.actual_amount - f.final_amount,
                            0,
                          );
                          return (
                            <tr key={f.id}>
                              <td>
                                <span className="d-flex align-items-center gap-1">
                                  <i className="ri-price-tag-3-line text-secondary"></i>
                                  {f.fee_type}
                                  {excluded && (
                                    <span className="badge bg-secondary bg-opacity-10 text-secondary ms-1">
                                      No Discount
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td>{fmtMoney(f.actual_amount)}</td>
                              <td className="text-warning">
                                {fmtMoney(rowDiscount)}
                              </td>
                              <td className="fw-semibold">
                                {fmtMoney(f.final_amount)}
                              </td>
                              <td className="text-success">
                                {fmtMoney(f.paid_amount)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="fw-semibold">
                          <td>Total</td>
                          <td>{fmtMoney(totalActual)}</td>
                          <td className="text-warning">
                            {fmtMoney(totalDiscount)}
                          </td>
                          <td>{fmtMoney(totalFinal)}</td>
                          <td className="text-success">
                            {fmtMoney(totalPaid)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                <label className="form-label fw-semibold d-flex align-items-center gap-1">
                  <i className="ri-message-2-line text-secondary"></i>
                  Discount Remark
                </label>
                <textarea
                  className="form-control mb-3"
                  rows={2}
                  value={discountRemark}
                  onChange={(e) => setDiscountRemark(e.target.value)}
                  placeholder="Reason for discount"
                ></textarea>

                <div className="d-flex gap-2 justify-content-end">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setMode("view")}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={handleUpdateDiscount}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ri-save-line me-1"></i>
                        Save Discount
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>

          {mode === "view" && (
            <div className="modal-footer border-0 pt-0">
              {hasAccess("/student/batch/discount/up") && (
                <button
                  className="btn btn-outline-warning"
                  onClick={() => setMode("discount")}
                >
                  <i className="ri-coupon-line me-1"></i>
                  Update Discount
                </button>
              )}
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
